import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { css, styled } from "goober";
import { Modal } from "clutch/src/Modal/Modal.jsx";
import { cssDialog } from "clutch/src/Modal/styles.mjs";

import { classNames } from "@/util/class-names.mjs";
import getRootStyleProperty from "@/util/get-root-style-property.mjs";
import globals from "@/util/global-whitelist.mjs";
import useForwardedRef from "@/util/use-forwarded-ref.mjs";
import { useSharedScope } from "@/util/use-shared-scope.mjs";

// Reads the animation duration from the stylesheet and returns in milliseconds
export const getAnimationDuration = () => {
  const [duration] = getRootStyleProperty("--transition").split(" ") ?? [""];
  const mod = duration.endsWith("ms") ? 1 : duration.endsWith("s") ? 1000 : 0;
  return mod * parseFloat(duration);
};

const CLOSED_CLASS = "modal-closed";
const OPEN_CLASS = "modal-open";

type FullscreenOverlayProps = React.ComponentPropsWithoutRef<"dialog"> & {
  /**
   * Toggle the modal open or closed. It is not recommended to use _both_ this
   * prop and the `ref` to show or hide the modal, as they will likely conflict.
   */
  isOpen?: boolean;

  /**
   * Make the modal unclosable by normal methods, such as clicking the backdrop.
   */
  isPersistent?: boolean;

  /**
   * Fired when the modal is shown.
   */
  onModalShow?: () => void;

  /**
   * Fired when the modal is closed for any reason, including, but not limited to,
   * clicking the backdrop when `isPersistent` is `false` or pressing the browser's
   * back button.
   */
  onModalClose?: () => void;
};

function FullscreenOverlay(
  {
    isOpen,
    isPersistent,
    onModalShow: onModalShowProp,
    onModalClose: onModalCloseProp,
    className,
    children,
    ...restProps
  }: React.ComponentPropsWithRef<"dialog"> & FullscreenOverlayProps,
  forwardedRef: React.ForwardedRef<HTMLDialogElement>,
) {
  const ref = useForwardedRef(forwardedRef);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;

    // for cleanup
    const timeouts: Timeout[] = [];
    const frames: number[] = [];

    const obs = new MutationObserver((mutations) => {
      for (const { removedNodes } of mutations) {
        // If our container is removed we want to animate it out
        // so we add it back to the tree to play the animation then remove it after completion
        for (const node of Array.from(removedNodes)) {
          if (!(node instanceof Element)) continue;

          // we have to attach a new element because clutch Modal won't render its children when !show
          // we do not have position-fixed so we will attach to a new 0 size element
          const app_container = element.parentElement;
          const new_parent = globals.document.createElement("dialog");

          new_parent.classList.add(cssDialog(), cssFixedModal(), "closing");
          if (className) new_parent.classList.add(className);

          new_parent.appendChild(node);
          app_container.appendChild(new_parent);

          new_parent.showModal();

          const transitionElements = [node, new_parent];
          transitionElements.forEach((el) => {
            el.classList.remove(CLOSED_CLASS);
            el.classList.add(OPEN_CLASS);
          });

          // we have to delay adding closed animation because the element moved in the tree,
          // to start the animation it has to first have no animated class then add next tick
          frames.push(
            requestAnimationFrame(() => {
              transitionElements.forEach((el) => {
                el.classList.remove(OPEN_CLASS);
                el.classList.add(CLOSED_CLASS);
              });

              // we have exposed transition duration from styles
              // so we know exactly how long we have to display this animation before it is invisible
              timeouts.push(
                setTimeout(() => new_parent.remove(), getAnimationDuration()),
              );
            }),
          );
        }

        // for open attribute we animate the inner based on open state
        // note that we don't have to check this value changed to conditionally set state;
        // if this is "open" we know its value changed
      }
    });

    obs.observe(element, { attributeFilter: ["open"], childList: true });

    return () => {
      obs.disconnect();
      timeouts.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [className, ref]);

  const scope = useSharedScope({
    name: "fullscreen-overlay",
    onPop: useCallback(() => {
      if (isPersistent) return;

      ref.current?.close();
    }, [ref, isPersistent]),
  });

  const [transitionClass, setTransitionClass] = useState(CLOSED_CLASS);

  const onModalShow = useCallback(() => {
    scope.claim();

    setTransitionClass(OPEN_CLASS);
    onModalShowProp?.();
  }, [scope, onModalShowProp]);

  const onModalClose = useCallback(() => {
    scope.clear();

    setTransitionClass(CLOSED_CLASS);
    onModalCloseProp?.();
  }, [scope, onModalCloseProp]);

  const handleClickBackdrop = useCallback(
    (e) => {
      if (isPersistent) return;

      if (e.currentTarget === e.target) {
        ref.current?.close();
      }
    },
    [isPersistent, ref],
  );

  useEffect(() => {
    if (isOpen) {
      if (ref.current?.open) return;

      ref.current?.showModal();
    } else {
      if (!ref.current?.open) return;

      ref.current?.close();
    }
  }, [ref, isOpen]);

  return (
    <Modal
      ref={ref}
      blockClose={isPersistent}
      onModalShow={onModalShow}
      onModalClose={onModalClose}
      {...classNames(
        className,
        cssFixedModal(),
        transitionClass,
        transitionClass === OPEN_CLASS && cssBackdrop(),
      )}
      {...restProps}
    >
      <ModalContainer onClick={handleClickBackdrop}>{children}</ModalContainer>
    </Modal>
  );
}

export default forwardRef(FullscreenOverlay);

const cssFixedModal = () => css`
  left: 0;
  right: 0;
  bottom: 0;

  width: 100vw;
  height: 100vh;

  width: 100dvw;
  height: 100dvh;

  overflow: hidden;

  /* override dialog styles */
  max-width: none;
  max-height: none;
  margin-bottom: 0;

  clip-path: inset(var(--title-bar-height) 0 0 0);

  &.notification-visible {
    /* Match notification height */
    height: calc(100vh - var(--sp-8));
    height: calc(100dvh - var(--sp-8));
  }

  &.${CLOSED_CLASS} {
    &::backdrop {
      background-color: transparent;

      /* Prefer NOT animating the filter because css is buggy with edges */
      opacity: 0;
    }
  }

  /* When we are closing we want to show ABOVE all other elements */
  &.closing,
  &.closing::backdrop {
    display: block;

    z-index: 10;

    pointer-events: none !important;

    * {
      pointer-events: none !important;
    }
  }
`;

const cssBackdrop = () => css`
  &::backdrop {
    transition: ${getRootStyleProperty("--transition")};
    transition-property: background-color, opacity;
    background-color: ${getRootStyleProperty("--shade10-75")} !important;
    backdrop-filter: blur(16px);
  }
`;

const ModalContainer = styled("div")`
  display: flex;

  width: 100%;
  height: 100%;

  overflow: hidden;

  transition: var(--transition);
  transition-property: opacity, transform, margin;

  cursor: pointer;

  & > * {
    cursor: auto;
  }

  &.${CLOSED_CLASS} {
    transform: translateY(100vh);
    transform: translateY(100dvh);

    /* very fun: clients force inputs to be visible at all times, taking precedence over transform  */
    /* this somehow fixes it and doesn't even change how they are displayed!! */
    input:focus {
      transform: translateY(-100vh);
      transform: translateY(-100dvh);
    }
  }
`;
